import React from 'react';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import AddressFields, { EMPTY_ADDRESS } from './AddressFields';
import { BENEFICIARY_RELATIONSHIPS, MAX_BENEFICIARIES } from './options';
import { formatCents, rowHasData, sumCents } from './percent';
import { MIN_DOB, todayIso } from './format';

export const EMPTY_BENEFICIARY = {
  pct: '',
  relationship: '',
  full_name: '',
  dob: '',
  address: { ...EMPTY_ADDRESS },
};

/**
 * Repeatable beneficiary table with a live percentage total.
 *
 * Follows the auto-grow idiom used by the FPR wizard's account/debt lists:
 * a blank row is appended as soon as the last one has data, so there is no
 * "Add another" button to hunt for.
 */
export default function BeneficiaryRows({
  idPrefix,
  title,
  description,
  entries,
  onChange,
  sameAsAddress,
}) {
  const base = (entries && entries.length > 0)
    ? entries
    : [{ ...EMPTY_BENEFICIARY }];

  const lastHasData = rowHasData(base[base.length - 1]);
  const rows = lastHasData && base.length < MAX_BENEFICIARIES
    ? [...base, { ...EMPTY_BENEFICIARY }]
    : base;

  function updateRow(idx, field, val) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  }

  function removeRow(idx) {
    const next = rows.filter((_r, i) => i !== idx);
    onChange(next.length > 0 ? next : [{ ...EMPTY_BENEFICIARY }]);
  }

  const totalCents = sumCents(rows);
  const anyFilled = rows.some(rowHasData);
  const isExact = totalCents === 10000;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="cashflow-subsection-title mb-0">{title}</div>
        {anyFilled && (
          <span
            className={`badge ${isExact ? 'bg-success' : 'bg-danger'}`}
            title="Percentages must total exactly 100.00%"
          >
            Total: {formatCents(totalCents)}%
          </span>
        )}
      </div>

      {description && (
        <p className="text-muted small">{description}</p>
      )}

      {rows.map((row, idx) => (
        <div className="cashflow-section-card mb-3" key={idx}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold small text-muted">
              {title} Beneficiary #{idx + 1}
            </span>
            {rows.length > 1 && (
              <button
                type="button"
                className="btn btn-link btn-sm text-danger p-0"
                onClick={() => removeRow(idx)}
                title="Remove this beneficiary"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>

          <div className="row g-2">
            <TextInput
              id={`${idPrefix}-${idx}-name`}
              label="Full Name"
              col="col-md-6"
              value={row.full_name}
              onChange={(v) => updateRow(idx, 'full_name', v)}
              autoComplete="off"
            />
            <SelectInput
              id={`${idPrefix}-${idx}-relationship`}
              label="Relationship"
              col="col-md-3"
              options={BENEFICIARY_RELATIONSHIPS}
              value={row.relationship}
              onChange={(v) => updateRow(idx, 'relationship', v)}
            />
            <TextInput
              id={`${idPrefix}-${idx}-pct`}
              label="Percentage"
              type="number"
              step="0.01"
              col="col-md-3"
              placeholder="e.g. 33.34"
              value={row.pct}
              onChange={(v) => updateRow(idx, 'pct', v)}
            />
            <TextInput
              id={`${idPrefix}-${idx}-dob`}
              label="Date of Birth"
              type="date"
              col="col-md-6"
              min={MIN_DOB}
              max={todayIso()}
              value={row.dob}
              onChange={(v) => updateRow(idx, 'dob', v)}
            />
            <div className="col-12">
              <AddressFields
                idPrefix={`${idPrefix}-${idx}-addr`}
                legend="Mailing Address"
                sameAsAddress={sameAsAddress}
                sameAsLabel="Same mailing address as mine"
                value={row.address}
                onChange={(v) => updateRow(idx, 'address', v)}
              />
            </div>
          </div>
        </div>
      ))}

      {rows.length >= MAX_BENEFICIARIES && (
        <p className="text-muted small mb-0">
          You can list up to {MAX_BENEFICIARIES} {title.toLowerCase()}{' '}
          beneficiaries here. If you need more, let your advisor know.
        </p>
      )}
    </div>
  );
}
